import React, { useRef, useState } from "react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CodexIcon from "@/assets/images/codex.svg?react";
import VscodeIcon from "@/assets/images/vscode.svg?react";
import ClaudeIcon from "@/assets/images/claude.svg?react";
import PythonIcon from "@/assets/images/python.svg?react";
import { TooltipButton } from "@/components/common/TooltipButton";
import { CodeBlock } from "@/components/common/CodeBlock";
import { CopyButton } from "@/components/common/CopyButton";
import { RowOverlay } from "@/components/common/RowOverlay";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import {
  mcpServerUrl,
  isLocalEnv,
  TOKEN_PLACEHOLDER,
  vscodeDeepLink,
  vscodeServerEntry,
  claudeCodeCmd,
  claudeDesktopEntry,
  codexCmd,
  mcpServerEntry,
} from "./mcpConnect";

const PYMYCORR_GITHUB = "https://github.com/recons-ltd/pymycorr";
const PYMYCORR_PYPI = "https://pypi.org/project/pymycorr/";
const PYMYCORR_QUICKSTART = `pip install pymycorr
export MYCORR_API_TOKEN=${TOKEN_PLACEHOLDER}`;

const ICON_PROPS = {
  width: 20,
  height: 20,
  className: "text-fg-muted",
  "aria-hidden": true,
} as const;

const CARD_CLASS =
  "flex flex-col gap-1 p-3 border rounded-app text-left cursor-pointer select-none transition-colors";
const cardTone = (active: boolean) =>
  active
    ? "border-accent bg-bg-hover"
    : "border-border bg-transparent hover:border-accent hover:bg-bg-hover";

const STEP_TEXT = "text-fg-muted-regular leading-relaxed";

type Expandable = "codex" | "vscode" | "claude" | "python" | "other";

interface ClientCard {
  id: Expandable;
  label: string;
  description: string;
  ariaLabel: string;
  icon: React.ReactNode;
}

const CLIENT_CARDS: ClientCard[] = [
  {
    id: "codex",
    label: "Codex",
    description: "One terminal command.",
    ariaLabel: "Connect Codex",
    icon: <CodexIcon {...ICON_PROPS} />,
  },
  {
    id: "vscode",
    label: "VS Code",
    description: "GitHub Copilot.",
    ariaLabel: "Connect VS Code",
    icon: <VscodeIcon {...ICON_PROPS} />,
  },
  {
    id: "claude",
    label: "Claude",
    description: "Desktop or claude.ai connector.",
    ariaLabel: "Connect Claude",
    icon: <ClaudeIcon {...ICON_PROPS} />,
  },
  {
    id: "python",
    label: "Python",
    description: "pymycorr SDK + REST API.",
    ariaLabel: "Connect Python",
    icon: <PythonIcon {...ICON_PROPS} />,
  },
  {
    id: "other",
    label: "Other",
    description: "ChatGPT, Cursor, Windsurf…",
    ariaLabel: "Other clients",
    icon: <MoreHorizIcon sx={{ fontSize: 20 }} className="text-fg-muted" />,
  },
];

export const AccessConnectPanel: React.FC = () => {
  const [expanded, setExpanded] = useState<Expandable | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);
  // Connections are org-gated: every snippet advertises the ACTIVE org's
  // alias URL, matching the context the rest of the app is showing.
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  const toggle = (id: Expandable, card: HTMLElement) => {
    if (expanded === id) {
      setExpanded(null);
      return;
    }
    setAnchorEl(card);
    setExpanded(id);
  };

  // Card bodies must render off `shown`, never `expanded` — the last-opened card
  // stays mounted so the panel can animate out instead of vanishing.
  const lastShown = useRef<Expandable | null>(null);
  if (expanded) lastShown.current = expanded;
  const shown = expanded ?? lastShown.current;
  const shownCard = CLIENT_CARDS.find((c) => c.id === shown);

  // Null only while the org store hydrates: a logged-in account always has
  // an org (D36's lazy personal-org mint at login), so this is a loading
  // state, not a reachable no-org UI.
  if (!activeOrgId) return null;

  const launchDeepLink = (url: string, appLabel: string) => {
    let opened = false;
    const markOpened = () => {
      opened = true;
    };
    window.addEventListener("blur", markOpened, { once: true });

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url;
    document.body.appendChild(iframe);

    window.setTimeout(() => {
      iframe.remove();
      window.removeEventListener("blur", markOpened);
      if (!opened && !document.hidden) {
        showNotificationGlobal(
          `If ${appLabel} didn't open, make sure it's installed — or pick "Other" to paste the server URL.`,
          "default",
        );
      }
    }, 1500);
  };

  const local = isLocalEnv();

  const urlRow = (
    <div className="flex items-center gap-2 border border-border rounded-app bg-bg px-3 py-2">
      <span className="font-mono text-mono-small text-fg truncate select-text">
        {mcpServerUrl(activeOrgId)}
      </span>
      <div className="ml-auto shrink-0">
        <CopyButton
          text={mcpServerUrl(activeOrgId)}
          ariaLabel="Copy server URL"
          onCopy={() => showNotificationGlobal("Server URL copied", "success")}
        />
      </div>
    </div>
  );

  return (
    <div className="border border-border bg-bg-subtle rounded-app p-4 flex flex-col gap-3">
      <div>
        <h2 className="text-fg-bold leading-none">Connect a client</h2>
        <p className="text-fg-muted-regular mt-1">
          Pick your client to get connected.
        </p>
      </div>

      <div>
        <div
          ref={setGridEl}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2"
        >
          {CLIENT_CARDS.map((card) => (
            <button
              key={card.id}
              type="button"
              aria-label={card.ariaLabel}
              aria-expanded={expanded === card.id}
              className={`${CARD_CLASS} ${cardTone(expanded === card.id)}`}
              onClick={(e) => toggle(card.id, e.currentTarget)}
            >
              {card.icon}
              <span className="text-fg-bold">{card.label}</span>
              <span className="text-fg-muted-regular-small">
                {card.description}
              </span>
            </button>
          ))}
        </div>

        {/* Opens below the card, not over it: the accent-bordered card is what
            says which client these steps are for, so it has to stay visible. */}
        <RowOverlay
          open={expanded !== null}
          onClose={() => setExpanded(null)}
          anchor={anchorEl}
          spanFrom={gridEl}
          placement="below"
          ariaLabel={`Connect ${shownCard?.label ?? ""}`}
        >
          <div className="flex flex-col gap-2 px-1 pb-1">
            {shown === "codex" && (
              <>
                <p className={STEP_TEXT}>
                  {local
                    ? "Run this — a browser opens to authorize:"
                    : "Run this in your terminal:"}
                </p>
                <CodeBlock
                  code={codexCmd(activeOrgId)}
                  ariaLabel="Shell: codex mcp add for MyCorr"
                  className="bg-bg! border-border select-text"
                />
              </>
            )}

            {shown === "vscode" && (
              <>
                {!local && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-fg-semibold">Install in Copilot</span>
                    <TooltipButton
                      title="Open in VS Code"
                      variant="default"
                      size="xs"
                      aria-label="Install in VS Code"
                      onClick={() =>
                        launchDeepLink(vscodeDeepLink(activeOrgId), "VS Code")
                      }
                    >
                      <OpenInNewIcon />
                    </TooltipButton>
                  </div>
                )}
                <p className={STEP_TEXT}>
                  {local ? "Add to " : "Or add manually to "}
                  <code className="font-mono">.vscode/mcp.json</code>, inside
                  its <code className="font-mono">servers</code> object
                  {local ? " — a browser opens to authorize:" : ":"}
                </p>
                <CodeBlock
                  code={vscodeServerEntry(activeOrgId)}
                  ariaLabel="JSON: VS Code servers entry for MyCorr"
                  className="bg-bg! border-border select-text"
                />
              </>
            )}

            {shown === "claude" && (
              <>
                {local ? (
                  <>
                    <p className={STEP_TEXT}>
                      In Claude Desktop open{" "}
                      <span className="text-fg-semibold">
                        Settings → Developer → Edit Config
                      </span>
                      , paste this inside the{" "}
                      <code className="font-mono">mcpServers</code> object, and
                      relaunch. A browser opens to authorize:
                    </p>
                    <CodeBlock
                      code={claudeDesktopEntry(activeOrgId)}
                      ariaLabel="JSON: Claude Desktop mcpServers entry for MyCorr"
                      className="bg-bg! border-border select-text"
                    />
                  </>
                ) : (
                  <>
                    <p className={STEP_TEXT}>
                      <span className="text-fg-semibold">
                        Settings → Connectors → Add custom connector
                      </span>
                      , paste this URL, then authorize.
                    </p>
                    {urlRow}
                  </>
                )}
                <p className={STEP_TEXT}>
                  Using Claude Code (CLI)? Run this instead:
                </p>
                <CodeBlock
                  code={claudeCodeCmd(activeOrgId)}
                  ariaLabel="Shell: claude mcp add for MyCorr"
                  className="bg-bg! border-border select-text"
                />
              </>
            )}

            {shown === "python" && (
              <>
                <p className={STEP_TEXT}>Generate a token below, then:</p>
                <CodeBlock
                  code={PYMYCORR_QUICKSTART}
                  placeholder={TOKEN_PLACEHOLDER}
                  ariaLabel="Shell: install pymycorr and set the token"
                  className="bg-bg! border-border select-text"
                />
                <div className="flex items-center gap-2 text-fg-muted-regular-small">
                  <a
                    href={PYMYCORR_GITHUB}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline"
                  >
                    GitHub
                  </a>
                  <span aria-hidden="true">·</span>
                  <a
                    href={PYMYCORR_PYPI}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline"
                  >
                    PyPI
                  </a>
                </div>
              </>
            )}

            {shown === "other" && (
              <>
                {!local && (
                  <>
                    <p className={STEP_TEXT}>
                      Paste this URL (ChatGPT, and other connector-based
                      clients), then authorize:
                    </p>
                    {urlRow}
                  </>
                )}
                <p className={STEP_TEXT}>
                  {local
                    ? "Paste this inside the "
                    : "Or paste this inside the "}
                  <code className="font-mono">mcpServers</code> object of your
                  client's MCP config (Cursor, Windsurf, Zed, any MCP client)
                  {local ? " — a browser opens to authorize:" : ":"}
                </p>
                <CodeBlock
                  code={mcpServerEntry(activeOrgId)}
                  ariaLabel="JSON: mcpServers entry for MyCorr"
                  className="bg-bg! border-border select-text"
                />
              </>
            )}
          </div>
        </RowOverlay>
      </div>
    </div>
  );
};
